import React, { useState, useEffect } from 'react';
import { AuditLog } from '../data/mockData';
import { colors } from '../theme';
import api from '../services/api';

const AuditLogs: React.FC = () => {
  const [allLogs, setAllLogs] = useState<AuditLog[]>([]);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.auditLogs.getAll()
      .then(res => {
        const data = res.data || res;
        if (Array.isArray(data)) setAllLogs(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const actions = ['all', ...Array.from(new Set(allLogs.map(l => l.action)))];

  const filtered = allLogs.filter(l => {
    if (actionFilter !== 'all' && l.action !== actionFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return l.actor.toLowerCase().includes(q) || l.target.toLowerCase().includes(q) || l.details.toLowerCase().includes(q);
    }
    return true;
  });

  const getActionColor = (action: string) => {
    if (action.includes('Approved')) return colors.success;
    if (action.includes('Rejected')) return colors.error;
    if (action.includes('Submitted')) return colors.info;
    if (action.includes('Login')) return colors.gold;
    if (action.includes('Emergency')) return colors.error;
    return colors.textMuted;
  };

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>Audit Logs</h2>
            <p>Complete system activity trail</p>
          </div>
        </div>
        <div className="header-right">
          <div className="search-bar">
            <span>🔍</span>
            <input placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="header-btn primary">↓ Export Logs</button>
        </div>
      </div>

      <div className="content">
        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>}
        <div className="card">
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #E8ECF2' }}>
            <div className="filter-bar">
              {actions.map(a => (
                <button key={a} className={`filter-chip${actionFilter === a ? ' active' : ''}`}
                  onClick={() => setActionFilter(a)}>
                  {a === 'all' ? 'All Actions' : a}
                </button>
              ))}
            </div>
          </div>

          <div className="card-body">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Target</th>
                    <th>Details</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(log => (
                    <tr key={log.id}>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                        {new Date(log.timestamp).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: '2-digit',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td style={{ fontWeight: 600 }}>{log.actor}</td>
                      <td>
                        <span style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                          background: `${getActionColor(log.action)}15`,
                          color: getActionColor(log.action)
                        }}>
                          {log.action}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{log.target}</td>
                      <td style={{ fontSize: 12, color: colors.textSecondary, maxWidth: 300 }}>{log.details}</td>
                      <td style={{ fontSize: 12, color: colors.textMuted, fontFamily: 'monospace' }}>{log.ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">Showing {filtered.length} entries</div>
              <div className="pagination-btns">
                <button className="page-btn active">1</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuditLogs;
